import { Link } from "react-router-dom";
import { AlertTriangle, ChevronLeft, ChevronRight, Inbox, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { titleCase } from "@/lib/format";

/* ------------------------------------------------------------------ page header */
export const PageHeader = ({ eyebrow, title, description, actions, backTo, backLabel, testId = "admin-page-header" }) => (
  <header className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between" data-testid={testId}>
    <div className="min-w-0">
      {backTo && (
        <Link to={backTo} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3" data-testid="admin-back-link">
          <ChevronLeft className="h-3.5 w-3.5" /> {backLabel || "Back"}
        </Link>
      )}
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      <h1 className="font-display text-4xl lg:text-5xl font-medium tracking-[-0.01em] mt-1 truncate" data-testid="admin-page-title">{title}</h1>
      {description && <p className="text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed">{description}</p>}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2 shrink-0">{actions}</div>}
  </header>
);

/* ------------------------------------------------------------------ cards */
export const SectionCard = ({ title, description, actions, children, className, testId }) => (
  <section className={cn("bg-card border rounded-xl shadow-[0_1px_0_hsl(var(--shadow-1))]", className)} data-testid={testId}>
    {(title || actions) && (
      <div className="flex items-start justify-between gap-4 px-5 py-4 border-b">
        <div>
          {title && <h2 className="text-sm font-semibold tracking-tight">{title}</h2>}
          {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
        </div>
        {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
      </div>
    )}
    <div className="p-5">{children}</div>
  </section>
);

export const Stat = ({ label, value, hint, tone = "default", testId, to }) => {
  const body = (
    <div className={cn("bg-card border rounded-xl p-5 shadow-[0_1px_0_hsl(var(--shadow-1))] h-full", to && "hover:shadow-[0_10px_30px_hsl(var(--shadow-2))] transition-shadow")} data-testid={testId}>
      <p className="eyebrow">{label}</p>
      <p className={cn("font-display text-4xl font-medium mt-2 tabular", tone === "warn" && "text-[hsl(var(--accent-ochre))]", tone === "danger" && "text-destructive", tone === "good" && "text-[hsl(var(--accent-forest))]")}>{value ?? "—"}</p>
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
  return to ? <Link to={to} className="block">{body}</Link> : body;
};

/* ------------------------------------------------------------------ status badges */
const TONES = {
  good: "bg-[hsl(var(--accent-forest))]/10 text-[hsl(var(--accent-forest))] border-[hsl(var(--accent-forest))]/25",
  warn: "bg-[hsl(var(--accent-ochre))]/10 text-[hsl(var(--accent-ochre))] border-[hsl(var(--accent-ochre))]/30",
  muted: "bg-muted text-muted-foreground border-border",
  neutral: "bg-secondary text-foreground border-border",
  danger: "bg-destructive/10 text-destructive border-destructive/25",
  ink: "bg-foreground text-background border-foreground",
};
const STATUS_TONE = {
  published: "good", active: "good", applied: "good", received: "good", posted: "good", live: "good", ok: "good", granted: "good",
  draft: "neutral", open: "warn", submitted: "warn", partially_received: "warn", test: "warn", applying: "warn", processing: "warn", low: "warn",
  archived: "muted", disabled: "muted", cancelled: "muted", closed: "muted", not_configured: "muted", inactive: "muted",
  out: "danger", locked: "danger", error: "danger", unavailable: "danger",
};
export const StatusBadge = ({ status, label, tone, className, testId }) => {
  const key = String(status || "").toLowerCase();
  const t = tone || STATUS_TONE[key] || "neutral";
  return (
    <Badge variant="outline" className={cn("rounded-full font-medium text-[11px] tracking-wide px-2.5 py-0.5 whitespace-nowrap", TONES[t], className)} data-testid={testId || `status-badge-${key || "none"}`}>
      {label || titleCase(status || "—")}
    </Badge>
  );
};
export const DemoBadge = ({ className }) => <Badge className={cn("rounded-full bg-foreground text-background text-[10px] tracking-[0.12em] px-2 py-0 hover:bg-foreground", className)} data-testid="demo-badge">DEMO</Badge>;

export const StockPill = ({ available, reorderPoint }) => {
  const a = Number(available || 0);
  const rp = reorderPoint ?? 2;
  if (a <= 0) return <StatusBadge status="out" label="Out of stock" />;
  if (a <= rp) return <StatusBadge status="low" label={`Low · ${a}`} />;
  return <StatusBadge status="ok" label={`${a} available`} />;
};

/* ------------------------------------------------------------------ states */
export const EmptyState = ({ icon: Icon = Inbox, title, description, action, testId = "admin-empty-state" }) => (
  <div className="flex flex-col items-center justify-center text-center py-16 px-6" data-testid={testId}>
    <div className="h-12 w-12 rounded-full bg-secondary flex items-center justify-center mb-4"><Icon className="h-5 w-5 text-muted-foreground" /></div>
    <h3 className="font-display text-2xl font-medium">{title}</h3>
    {description && <p className="text-sm text-muted-foreground mt-2 max-w-md">{description}</p>}
    {action && <div className="mt-6">{action}</div>}
  </div>
);

export const ErrorState = ({ message, onRetry, testId = "admin-error-state" }) => (
  <div className="flex flex-col items-center justify-center text-center py-16 px-6" data-testid={testId}>
    <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center mb-4"><AlertTriangle className="h-5 w-5 text-destructive" /></div>
    <h3 className="font-display text-2xl font-medium">Something went wrong</h3>
    <p className="text-sm text-muted-foreground mt-2 max-w-md" data-testid="admin-error-message">{message}</p>
    {onRetry && <Button variant="outline" className="mt-6" onClick={onRetry} data-testid="admin-error-retry-button"><RefreshCw className="h-4 w-4 mr-2" />Try again</Button>}
  </div>
);

export const TableSkeleton = ({ rows = 6, cols = 5 }) => (
  <Table>
    <TableBody>
      {Array.from({ length: rows }).map((_, r) => (
        <TableRow key={r}>
          {Array.from({ length: cols }).map((__, c) => (
            <TableCell key={c} className="py-3"><Skeleton className="h-4 w-full" /></TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

export const Pager = ({ page, pages, total, onPage, pageSize }) => {
  if (!pages || pages <= 1) {
    return total !== undefined ? <p className="text-xs text-muted-foreground px-1" data-testid="admin-table-pagination">{total} record{total === 1 ? "" : "s"}</p> : null;
  }
  return (
    <div className="flex items-center justify-between gap-4 px-1" data-testid="admin-table-pagination">
      <p className="text-xs text-muted-foreground">{total} records{pageSize ? ` · ${pageSize} per page` : ""}</p>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="admin-pagination-prev"><ChevronLeft className="h-4 w-4" /></Button>
        <span className="text-xs tabular text-muted-foreground">Page {page} of {pages}</span>
        <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid="admin-pagination-next"><ChevronRight className="h-4 w-4" /></Button>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ forms */
export const Field = ({ label, htmlFor, hint, error, required, children, className }) => (
  <div className={cn("space-y-1.5", className)}>
    {label && (
      <Label htmlFor={htmlFor} className="text-xs font-medium text-foreground/90">
        {label}{required && <span className="text-destructive ml-0.5">*</span>}
      </Label>
    )}
    {children}
    {hint && !error && <p className="text-[11px] text-muted-foreground leading-snug">{hint}</p>}
    {error && <p className="text-[11px] text-destructive leading-snug" data-testid="field-error">{error}</p>}
  </div>
);

export const ConfirmDialog = ({ open, onOpenChange, title, description, confirmLabel = "Confirm", destructive = false, onConfirm, busy = false, testId = "admin-confirm-dialog" }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent data-testid={testId}>
      <AlertDialogHeader>
        <AlertDialogTitle className="font-display text-2xl font-medium">{title}</AlertDialogTitle>
        {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="admin-confirm-cancel-button">Cancel</AlertDialogCancel>
        <AlertDialogAction
          disabled={busy}
          onClick={(e) => { e.preventDefault(); onConfirm?.(); }}
          className={cn(destructive && "bg-destructive text-destructive-foreground hover:bg-destructive/90")}
          data-testid="admin-confirm-accept-button"
        >
          {busy ? "Working…" : confirmLabel}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

/* ------------------------------------------------------------------ misc */
export const KeyValue = ({ items, testId }) => (
  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm" data-testid={testId}>
    {items.filter(Boolean).map(([k, v]) => (
      <div key={k} className="flex flex-col border-b border-dashed pb-2">
        <dt className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">{k}</dt>
        <dd className="mt-0.5 break-words">{v === null || v === undefined || v === "" ? <span className="text-muted-foreground">—</span> : v}</dd>
      </div>
    ))}
  </dl>
);

export const Thumb = ({ src, alt, className }) => (
  <div className={cn("h-10 w-8 rounded-md bg-secondary overflow-hidden shrink-0 border", className)}>
    {src ? <img src={src} alt={alt || ""} className="h-full w-full object-cover" loading="lazy" /> : null}
  </div>
);

export const thCls = "text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium h-10 whitespace-nowrap";
export const tdCls = "py-2.5 text-sm align-middle";
export const rowCls = "hover:bg-secondary/60 transition-colors";
